// src/features/passwords/hooks/usePasswordBulk.js
import { useCallback, useMemo, useState } from 'react';
import api from '../../../api';

/**
 * Manages the "Add New Password Entries" bulk form.
 *
 * Works for both resources by passing resourcePrefix:
 *   - '/passwords'
 *   - '/advice-passwords'
 */
export default function usePasswordBulk({ resourcePrefix, refresh, onSuccess }) {
  if (!resourcePrefix) {
    throw new Error('usePasswordBulk: resourcePrefix is required');
  }
  if (typeof refresh !== 'function') {
    throw new Error('usePasswordBulk: refresh (callback) is required');
  }

  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 1000);
  }, []);

  // form model
  const emptyRow = useMemo(
    () => ({
      user_code: '',
      site: '',
      email: '',
      password: '',
      main: false,
    }),
    []
  );
  const [newPasswordEntries, setNewPasswordEntries] = useState([emptyRow]);

  // ----- helpers -----
  const resetNewPasswordForm = useCallback(() => {
    setNewPasswordEntries([emptyRow]);
  }, [emptyRow]);

  const handleNewPasswordFieldChange = useCallback((index, field, value) => {
    setNewPasswordEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addNewPasswordEntry = useCallback(() => {
    setNewPasswordEntries((prev) => {
      const last = prev[prev.length - 1] || emptyRow;
      return [
        ...prev,
        {
          user_code: last.user_code,
          site: '',
          email: '',
          password: '',
          main: false,
        },
      ];
    });
  }, [emptyRow]);

  const removePasswordEntry = useCallback((index) => {
    setNewPasswordEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Basic client-side validation (optional but helpful)
  const validateRow = useCallback((row) => {
    const problems = [];
    if (!row.user_code || !row.user_code.trim()) problems.push('user_code');
    if (!row.site || !row.site.trim()) problems.push('site');
    if (!row.email || !row.email.trim()) problems.push('email');
    if (!row.password || !row.password.trim()) problems.push('password');
    return problems;
  }, []);

  const invalidRows = useMemo(() => {
    return newPasswordEntries
      .map((r, i) => ({ i, problems: validateRow(r) }))
      .filter((x) => x.problems.length > 0);
  }, [newPasswordEntries, validateRow]);

  const hasValidationIssues = invalidRows.length > 0;

  // ----- submit -----
  const handleCreateNewPasswords = useCallback(async () => {
    try {
      setError(null);

      // Optionally block submission on invalid rows
      if (hasValidationIssues) {
        throw new Error(
          `Please complete required fields on ${invalidRows.length} entr${invalidRows.length === 1 ? 'y' : 'ies'}.`
        );
      }

      console.log('📤 Sending bulk create request:', newPasswordEntries.length, 'entries');
      const response = await api.post(`${resourcePrefix}/create-bulk`, newPasswordEntries);
      console.log('📥 Bulk create response status:', response.status);

      // Accept either 200 OK or 201 Created as success
      if (response && (response.status === 200 || response.status === 201)) {
        const createdPasswords = Array.isArray(response.data) ? response.data : newPasswordEntries;
        console.log('✅ Created', createdPasswords.length, 'passwords, IDs:', createdPasswords.map((p) => p.id));

        showToast('Passwords created successfully!');

        setShowNewPasswordForm(false);
        resetNewPasswordForm();

        if (onSuccess) {
          console.log('📞 Calling onSuccess with created passwords');
          setTimeout(() => {
            onSuccess(createdPasswords);
          }, 0);
        } else {
          console.log('🔄 No onSuccess handler, calling refresh');
          await refresh();
        }

        return;
      }

      // Anything else: throw with as much detail as we can
      const detail =
        response?.data?.detail ||
        response?.data?.message ||
        `Unexpected status ${response?.status}`;
      throw new Error(detail);
    } catch (err) {
      console.error('❌ Bulk create error:', err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create passwords';
      setError(typeof detail === 'string' ? detail : 'Failed to create passwords');
    }
  }, [
    resourcePrefix,
    newPasswordEntries,
    refresh,
    resetNewPasswordForm,
    showToast,
    invalidRows,
    hasValidationIssues,
    onSuccess,
  ]);

  return {
    // visibility + feedback
    showNewPasswordForm,
    setShowNewPasswordForm,
    error,
    toastMessage,

    // form state
    newPasswordEntries,
    setNewPasswordEntries,
    handleNewPasswordFieldChange,
    addNewPasswordEntry,
    removePasswordEntry,
    resetNewPasswordForm,

    // validation
    invalidRows,
    hasValidationIssues,

    // submit
    handleCreateNewPasswords,
  };
}
